import _ from 'lodash';
import { useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress, Menu, MenuItem, Tooltip } from '@mui/material';
import moment from 'moment';
import { ReactComponent as DownloadIcon } from '@/img/bookings/download.svg';
import { ReactComponent as DownloadIconBlack } from '@/img/bookings/download-b.svg';
import { CSV_EXPORT_TYPES } from 'arbitrip-common/general/constants';
import BookingsActions from '@/actions/BookingsActions';
import Analytics from 'arbitrip-common/client/analytics';
import BookingsConstants from '@/constants/BookingsConstants';

const ExportButton = ({ profile, disabled, downloadStatus, activeFilters, activeTab }) => {
	const { t } = useTranslation();
	const [exportAnchorEl, setExportAnchorEl] = useState(null);

	const exportButtonRef = useRef(null);

	const priority_export_enabled = useMemo(() => {
		return _.get(profile, 'company.settings', false);
	}, [profile]);

	const onExportClick = (e) => {
		setExportAnchorEl(e.currentTarget);
	};

	const onExportClose = () => {
		setExportAnchorEl(null);
	};

	const onDownloadBookingsCsvClick = (exportType) => {
		const company_id = _.get(profile, 'company.id');
		const is_agent = _.get(profile, 'agent') || _.get(profile, 'super_agent');

		BookingsActions.downloadedBookingsCsv(is_agent, activeFilters, exportType, activeTab);
		Analytics.actions.interactions.downloadedBookingsCsv(company_id, is_agent, activeFilters);
	};

	const onExportMenuChoice = (exportType) => {
		onExportClose();
		onDownloadBookingsCsvClick(exportType);
	};

	const isBusy = () => downloadStatus === BookingsConstants.STATUS.BUSY;

	const tooltipTitle = useMemo(() => {
		const dateFormat = 'DD/MM/YYYY';

		if (activeFilters.after_date || activeFilters.before_date) {
			const start = activeFilters.after_date ? moment(activeFilters.after_date).format(dateFormat) : '...';
			const end = activeFilters.before_date ? moment(activeFilters.before_date).format(dateFormat) : '...';
			return `Export reservations - Check-in: ${start} - ${end}`;
		}

		if (activeFilters.cancellation_deadline_after || activeFilters.cancellation_deadline_before) {
			const start = activeFilters.cancellation_deadline_after
				? moment(activeFilters.cancellation_deadline_after).format(dateFormat)
				: '...';
			const end = activeFilters.cancellation_deadline_before
				? moment(activeFilters.cancellation_deadline_before).format(dateFormat)
				: '...';
			return `Export reservations - Deadline: ${start} - ${end}`;
		}

		return `Export reservations - Creation Date: ${moment().subtract(1, 'year').format(dateFormat)} - ${moment().format(
			dateFormat,
		)}`;
	}, [activeFilters]);

	return (
		<>
			<Tooltip title={tooltipTitle}>
				<div
					tabIndex={0}
					role="button"
					aria-disabled={disabled || isBusy()}
					aria-controls="dropdown-menu"
					aria-haspopup="true"
					onClick={(e) => {
						if (!disabled && !isBusy()) {
							priority_export_enabled ? onExportClick(e) : onDownloadBookingsCsvClick(CSV_EXPORT_TYPES.CSV);
						}
					}}
					ref={exportButtonRef}
					onKeyDown={(e) => {
						if (!disabled && !isBusy() && (e.key === 'Enter' || e.key === ' ')) {
							e.preventDefault();
							priority_export_enabled ? onExportClick(e) : onDownloadBookingsCsvClick(CSV_EXPORT_TYPES.CSV);
						}
					}}
					style={{
						cursor: disabled || isBusy() ? 'not-allowed' : 'pointer',
						display: 'flex',
						alignItems: 'center',
					}}
				>
					{isBusy() ? (
						<div className="busy-indicator">
							<CircularProgress size={16} />
						</div>
					) : (
						<DownloadIcon />
					)}
				</div>
			</Tooltip>
			{priority_export_enabled && (
				<Menu
					id="csv-dropdown-menu"
					anchorEl={exportAnchorEl}
					open={Boolean(exportAnchorEl)}
					onClose={onExportClose}
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'right',
					}}
					transformOrigin={{
						vertical: 'top',
						horizontal: 'right',
					}}
				>
					<MenuItem onClick={() => onExportMenuChoice(CSV_EXPORT_TYPES.CSV)}>
						<DownloadIconBlack className="menu-item-icon" /> {t('Bookings.Export.CSV')}
					</MenuItem>
					<MenuItem onClick={() => onExportMenuChoice(CSV_EXPORT_TYPES.PRIORITY)}>
						<DownloadIconBlack className="menu-item-icon" /> {t('Bookings.Export.Priority')}
					</MenuItem>
				</Menu>
			)}
		</>
	);
};

export default ExportButton;
